import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import TopBar from "../components/TopBar";
import { Modal, Field } from "./Fields";
import { toast } from "sonner";
import { UserPlus, Trash2, ShieldCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const ROLES = [
  { value: "owner", label: "Owner" },
  { value: "manager", label: "Farm Manager" },
  { value: "hand", label: "Farm Hand" },
  { value: "readonly", label: "Read-Only" },
];
const SCOPES = [
  { value: "livestock", label: "Livestock" },
  { value: "equipment", label: "Equipment" },
  { value: "yields_fields", label: "Yields / Fields" },
];

export default function TeamSettings() {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loadError, setLoadError] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", role: "readonly", scopes: [] });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const m = await api.get("/team/members").then((r) => r.data);
      setMembers(m);
    } catch (e) {
      setLoadError(e?.response?.status === 401 ? "auth" : (e?.response?.data?.detail || "Could not load team"));
      setMembers([]);
    }
    try {
      const i = await api.get("/team/invites").then((r) => r.data);
      setInvites(i);
    } catch {
      setInvites([]);
    }
  };
  useEffect(() => { load(); }, []);

  const isOwner = user?.role === "owner";

  const toggleScope = (s) => setForm((f) => ({
    ...f,
    scopes: f.scopes.includes(s) ? f.scopes.filter((x) => x !== s) : [...f.scopes, s],
  }));

  const submitInvite = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/team/invites", {
        email: form.email.trim().toLowerCase(),
        role: form.role,
        scopes: form.role === "hand" ? form.scopes : [],
      });
      toast.success(`Invited ${form.email}`);
      setForm({ email: "", role: "readonly", scopes: [] });
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invite failed");
    } finally {
      setBusy(false);
    }
  };

  const revokeInvite = async (id) => {
    if (!window.confirm("Revoke this invite?")) return;
    await api.delete(`/team/invites/${id}`);
    load();
  };

  const removeMember = async (id, name) => {
    if (!window.confirm(`Remove ${name} from the team?`)) return;
    try {
      await api.delete(`/team/members/${id}`);
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Remove failed");
    }
  };

  const updateMember = async (id, patch) => {
    try {
      await api.patch(`/team/members/${id}`, patch);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  return (
    <div>
      <TopBar
        title="Team & Roles"
        subtitle="Manage who can see what. The Owner is the only one who can invite or remove people."
        actions={
          isOwner && !loadError && (
            <button
              data-testid="invite-form"
              onClick={() => setOpen(true)}
              className="bg-[#BC4727] text-white px-5 py-2 font-bold uppercase tracking-wider text-sm hover:bg-[#9D391E] transition-colors flex items-center gap-2 mt-6"
            >
              <UserPlus className="w-4 h-4" /> Invite Person
            </button>
          )
        }
      />

      {loadError === "auth" && (
        <div className="bg-white border-2 border-[#1F1E1B] p-6 mb-6" data-testid="team-signin-required">
          <div className="font-display text-2xl font-black mb-2">Sign in required</div>
          <div className="text-sm text-[#66645E] mb-4">
            You need to sign in as the Owner to manage teammates. If this is your farm and no one has claimed it yet, sign in with Google to become the Owner.
          </div>
          <a href="/login" className="bg-[#1A4135] text-white px-5 py-2 text-sm font-bold uppercase tracking-wider inline-block" data-testid="team-signin-btn">Sign in</a>
        </div>
      )}
      {loadError && loadError !== "auth" && (
        <div className="bg-white border-2 border-[#BC4727] p-4 mb-6 text-sm text-[#BC4727]" data-testid="team-error">{loadError}</div>
      )}

      {!loadError && (
      <div className="bg-white border border-[#1F1E1B] overflow-x-auto mb-6" data-testid="team-members-table">
        <table className="ledger-table">
          <thead>
            <tr>
              <th>Name</th><th>Email</th><th>Role</th><th>Scopes</th><th>Status</th><th></th>
            </tr>
          </thead>
          <tbody>
            {members.map((m) => (
              <tr key={m.user_id} data-testid={`team-member-${m.user_id}`}>
                <td className="font-semibold">{m.name || "—"}</td>
                <td className="text-[#66645E]">{m.email}</td>
                <td>
                  {isOwner && m.user_id !== user.user_id ? (
                    <select
                      value={m.role}
                      onChange={(e) => updateMember(m.user_id, { role: e.target.value })}
                      className="border border-[#1F1E1B] px-2 py-1 text-xs"
                      data-testid={`role-select-${m.user_id}`}
                    >
                      {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                  ) : (
                    <span className="stamp">{ROLES.find((r) => r.value === m.role)?.label || m.role}</span>
                  )}
                </td>
                <td className="text-xs text-[#66645E]">
                  {m.role === "hand" ? (
                    isOwner ? (
                      <div className="flex flex-wrap gap-1">
                        {SCOPES.map((s) => (
                          <label key={s.value} className="inline-flex items-center gap-1 cursor-pointer text-[10px] uppercase font-bold">
                            <input
                              type="checkbox"
                              checked={(m.scopes || []).includes(s.value)}
                              onChange={() => {
                                const next = (m.scopes || []).includes(s.value)
                                  ? m.scopes.filter((x) => x !== s.value)
                                  : [...(m.scopes || []), s.value];
                                updateMember(m.user_id, { scopes: next });
                              }}
                              data-testid={`scope-${m.user_id}-${s.value}`}
                            /> {s.label}
                          </label>
                        ))}
                      </div>
                    ) : (m.scopes || []).map((s) => SCOPES.find((x) => x.value === s)?.label || s).join(", ") || "—"
                  ) : "—"}
                </td>
                <td>
                  <span className={m.active ? "text-[#1A4135] font-bold text-xs uppercase" : "text-[#BC4727] font-bold text-xs uppercase"}>
                    {m.active ? "Active" : "Disabled"}
                  </span>
                </td>
                <td className="text-right">
                  {isOwner && m.user_id !== user.user_id && (
                    <button
                      onClick={() => removeMember(m.user_id, m.name || m.email)}
                      className="p-1.5 border border-[#BC4727] text-[#BC4727] hover:bg-[#BC4727] hover:text-white"
                      data-testid={`remove-member-${m.user_id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      {isOwner && !loadError && invites.length > 0 && (
        <div className="bg-white border border-[#1F1E1B] overflow-x-auto mb-6" data-testid="team-invites-table">
          <div className="px-4 py-3 border-b border-[#1F1E1B] text-xs font-bold uppercase tracking-[0.18em] bg-[#FAF7EC]">
            Pending Invites
          </div>
          <table className="ledger-table">
            <thead><tr><th>Email</th><th>Role</th><th>Scopes</th><th>Invited</th><th></th></tr></thead>
            <tbody>
              {invites.map((i) => (
                <tr key={i.id}>
                  <td>{i.email}</td>
                  <td><span className="stamp">{ROLES.find((r) => r.value === i.role)?.label || i.role}</span></td>
                  <td className="text-xs">{(i.scopes || []).map((s) => SCOPES.find((x) => x.value === s)?.label || s).join(", ") || "—"}</td>
                  <td className="text-xs text-[#66645E]">{new Date(i.created_at).toLocaleDateString()}</td>
                  <td className="text-right">
                    <button
                      onClick={() => revokeInvite(i.id)}
                      className="p-1.5 border border-[#BC4727] text-[#BC4727] hover:bg-[#BC4727] hover:text-white"
                      data-testid={`revoke-invite-${i.id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="bg-[#FAF7EC] border border-[#E5E2D6] p-4 text-xs text-[#5C4A2B] flex gap-2 items-start">
        <ShieldCheck className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <div>
          Invites are single-use. When your teammate signs in with Google using the invited email, they&apos;ll auto-join as the assigned role.
        </div>
      </div>

      {open && (
        <Modal title="Invite Teammate" onClose={() => setOpen(false)}>
          <form onSubmit={submitInvite} className="space-y-4">
            <Field label="Email" required>
              <input
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="teammate@example.com"
                className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm"
                data-testid="invite-email-input"
              />
            </Field>
            <Field label="Role" required>
              <select
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm"
                data-testid="invite-role-select"
              >
                <option value="manager">Farm Manager</option>
                <option value="hand">Farm Hand</option>
                <option value="readonly">Read-Only</option>
                <option value="owner">Owner (co-owner)</option>
              </select>
            </Field>
            {form.role === "hand" && (
              <Field label="Scopes (at least one)">
                <div className="space-y-1.5">
                  {SCOPES.map((s) => (
                    <label key={s.value} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.scopes.includes(s.value)}
                        onChange={() => toggleScope(s.value)}
                        data-testid={`invite-scope-${s.value.replace("_", "-")}`}
                      /> {s.label}
                    </label>
                  ))}
                </div>
              </Field>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setOpen(false)} className="border border-[#1F1E1B] px-5 py-2 text-sm font-bold uppercase tracking-wider">Cancel</button>
              <button
                type="submit"
                disabled={busy || !form.email || (form.role === "hand" && form.scopes.length === 0)}
                className="bg-[#1A4135] text-white px-5 py-2 text-sm font-bold uppercase tracking-wider disabled:opacity-40"
                data-testid="invite-submit-btn"
              >
                {busy ? "Sending…" : "Send Invite"}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
